import { useState, useEffect } from 'react';
import { Box, VStack, HStack, Text, useMediaQuery, Badge } from 'native-base';
import { MOBILE_VIEW_WIDTH } from '../../../../utils/screens';
import CustomModal from '../../../../components/modal';
import MobileModelSheet from '../../../../components/actionSheet';
import CustomButton from '../../../../components/button';
import DateInputField from '../../../../components/dateInput';
import InputField from '../../../../components/input';
import Dropdown from '../../../../components/dropdown';
import { Toaster } from '../../../../components/toaster';
import { LoaderAnimation } from '../../../../components/spinner';
import { sendHotelEnquiryEmailAPI, checkEventCoordinatorAPI } from '../../../../api/apis/workflow';
import { newEventListingAPI } from '../../../../api/apis/event';
import { COLORS } from '../../../../utils/colors';
import CryptoJS from 'crypto-js';
import config from '../../../../utils/config';
import ConfirmationModal from './confirmationModal';

const EmailEnquiryModal = ({ isOpen, onClose, hotelIds, hotelList = [], onSuccess = () => {} }) => {
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
  const toaster = Toaster();
  const [isSending, setIsSending] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    event_id: '',
    preferred_slot: '',
    no_of_persons: '',
    event_date: '',
    event_to_date: ''
  });
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [eventOptions, setEventOptions] = useState([]);

  // Get selected hotels based on hotelIds
  const getSelectedHotels = () => {
    if (hotelIds === "all") {
      return hotelList;
    } else if (Array.isArray(hotelIds)) {
      return hotelList.filter(hotel => hotelIds.includes(hotel._id));
    }
    return [];
  };

  const selectedHotels = getSelectedHotels();

  // Utility function to get current user's role
  const getCurrentUserRole = () => {
    try {
      const content = sessionStorage.getItem("content");
      if (content) {
        const bytes = CryptoJS.AES.decrypt(content, config.userKey);
        const jsonString = bytes.toString(CryptoJS.enc.Utf8);
        const userInfo = JSON.parse(jsonString);
        return userInfo?.role || null;
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
    return null;
  };

  // Fetch events for dropdown
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const userRole = getCurrentUserRole();
        if (userRole === 'initiator') {
          // For initiator role, use checkEventCoordinatorAPI with response_type
          const response = await checkEventCoordinatorAPI('quick_meets');
          
          if (!response.error && response.data?.data?.data) {
            const events = response.data.data.data.map(event => ({
              value: event.event_id,
              label: event.event_name
            }));
            setEventOptions(events);
          }
        } else {
          // For admin and other roles, use existing newEventListingAPI
          const response = await newEventListingAPI(
            '', // type
            1, // page_no
            1000, // per_page
            'upcoming', // status
            '', // email
            'notification' // list_type
          );
          
          if (!response.error && response.data?.data?.data?.[0]?.events) {
            const events = response.data.data.data[0].events.map(event => ({
              value: event._id,
              label: event.event_name
            }));
            setEventOptions(events);
          }
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    if (isOpen) {
      fetchEvents();
    }
  }, [isOpen]);

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // event_id is now optional, no validation needed
    
    if (!formData.preferred_slot.trim()) {
      newErrors.preferred_slot = 'Preferred slot is required';
    }
    
    if (!formData.no_of_persons.trim()) {
      newErrors.no_of_persons = 'Number of persons is required';
    }
    
    if (!formData.event_date.trim()) {
      newErrors.event_date = 'Event From date is required';
    } else if (!validateEventDate(formData.event_date)) {
      newErrors.event_date = 'Event From date must be today or a future date';
    }
    
    if (!formData.event_to_date.trim()) {
      newErrors.event_to_date = 'Event To date is required';
    } else if (!validateEventDate(formData.event_to_date)) {
      newErrors.event_to_date = 'Event To date must be today or a future date';
    } else if (formData.event_date && formData.event_to_date) {
      // Validate that event_to_date is not before event_date
      const eventDate = new Date(formData.event_date);
      const eventToDate = new Date(formData.event_to_date);
      if (eventToDate < eventDate) {
        newErrors.event_to_date = 'Event To date must be on or after event From date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendEmail = () => {
    setShowErrors(true);
    
    if (!validateForm()) {
      return;
    }

    // Show confirmation modal instead of directly sending
    setShowConfirmation(true);
  };

  const handleConfirmSendEmail = async () => {
    setIsSending(true);
    try {
      LoaderAnimation.show();
      
      // Format date to DD-MM-YYYY format
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      
      const event_date = formatDate(formData.event_date);
      const event_to_date = formatDate(formData.event_to_date);
      
      // Prepare hotel IDs
      const hotelIdsToSend = hotelIds === "all" ? "all" : selectedHotels.map(hotel => hotel._id);
      
      const payload = {
        hotel_id: hotelIdsToSend,
        event_id: formData.event_id || undefined, // Only include if not empty
        preferred_slot: formData.preferred_slot,
        no_of_persons: formData.no_of_persons, // Keep as string as per your example
        event_date: event_date,
        event_to_date: event_to_date
      };
      
      const response = await sendHotelEnquiryEmailAPI(payload);
      
      if (response.error) {
        toaster(
          response.message || 'Failed to send email enquiry',
          'error',
          'error'
        );
      } else {
        toaster(
          response.data?.data?.message || `Email enquiry sent successfully to ${selectedHotels.length} hotel(s)`,
          'success',
          'success'
        );
        handleClose();
        onSuccess();
      }
    } catch (error) {
      console.error('Error sending email enquiry:', error);
      toaster('Error sending email enquiry', 'error', 'error');
    } finally {
      setIsSending(false);
      LoaderAnimation.hide();
      setShowConfirmation(false);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        event_id: '',
        preferred_slot: '',
        no_of_persons: '',
        event_date: '',
        event_to_date: ''
      });
      setErrors({});
      setShowErrors(false);
      setIsSending(false);
      setShowConfirmation(false);
    }
  }, [isOpen]);

  // Get today's date in YYYY-MM-DD format for the fromDate prop
  const getTodayDate = () => {
    const today = new Date();
    // Set to start of day to avoid timezone issues
    today.setHours(0, 0, 0, 0);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Validate that selected date is not in the past
  const validateEventDate = (dateString) => {
    if (!dateString) return true; // Allow empty for initial state
    
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return selectedDate >= today;
  };

  const handleClose = () => {
    setFormData({
      event_id: '',
      preferred_slot: '',
      no_of_persons: '',
      event_date: '',
      event_to_date: ''
    });
    setErrors({});
    setShowErrors(false);
    setIsSending(false);
    setShowConfirmation(false);
    onClose();
  };

  const isFormValid = () => {
    return formData.preferred_slot && 
           formData.no_of_persons && 
           formData.event_date &&
           formData.event_to_date &&
           Object.keys(errors).length === 0;
  };

  const modalBody = () => (
    <VStack space={4}>
      <Text
        fontSize="16px"
        fontWeight="medium"
        color={COLORS.PARTNERS_HEADING_COLOR}
        mb={2}
      >
        Send Email Enquiry
      </Text>
      
      {/* Selected Hotels Display */}
      <Box>
        <Text
          fontSize="14px"
          fontWeight="medium"
          color={COLORS.BLACK_COLOR}
          mb={2}
        >
          Selected Hotels ({selectedHotels?.length}):
        </Text>
        <Box maxH="120px" overflowY="auto" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}>
          {selectedHotels.map((hotel, index) => (
            <HStack key={hotel._id} space={2} mb={1} alignItems="center">
              <Badge
                colorScheme="blue"
                variant="subtle"
                size="sm"
              >
                {index + 1}
              </Badge>
              <Text fontSize="14px" color={COLORS.BLACK_COLOR}>
                {hotel.hotel_name} - {hotel.city}
              </Text>
            </HStack>
          ))}
        </Box>
      </Box>
      
      {/* Event Listing Dropdown */}
      <Box>
        <Dropdown
          label="Event (Optional)"
          name="event_id"
          selectedValues={formData.event_id}
          setSelectedValues={(value) => updateField('event_id', value)}
          dropItems={eventOptions}
          placeholder="Event (Optional)"
          isInvalid={showErrors && !!errors.event_id}
          errorMessage={showErrors ? errors.event_id : ''}
          forceRemount={isOpen}
        />
      </Box>

      {/* Preferred Slot Dropdown */}
      <Box>
        <Dropdown
          label="Preferred Slot"
          name="preferred_slot"
          selectedValues={formData.preferred_slot}
          setSelectedValues={(value) => updateField('preferred_slot', value)}
          dropItems={[
            { value: 'morning', label: 'Morning' },
            { value: 'evening', label: 'Evening' },
            { value: 'morning + evening', label: 'Morning + Evening' }
          ]}
          placeholder="Preferred Slot"
          isInvalid={showErrors && !!errors.preferred_slot}
          errorMessage={showErrors ? errors.preferred_slot : ''}
          important
          forceRemount={isOpen}
        />
      </Box>

      {/* Number of Persons Dropdown */}
      <Box>
        <Dropdown
          label="Number of Persons"
          name="no_of_persons"
          selectedValues={formData.no_of_persons}
          setSelectedValues={(value) => updateField('no_of_persons', value)}
          dropItems={[
            { value: '25 - 50', label: '25 - 50' },
            { value: '50 - 100', label: '50 - 100' },
            { value: '100 - 200', label: '100 - 200' },
            { value: 'Above 200', label: 'Above 200' }
          ]}
          placeholder="Number of Persons"
          isInvalid={showErrors && !!errors.no_of_persons}
          errorMessage={showErrors ? errors.no_of_persons : ''}
          important
          forceRemount={isOpen}
        />
      </Box>

      {/* Event Date and Event To Date Row */}
      <HStack space={4} direction={MobileWindow ? "column" : "row"}>
        <Box flex={1}>
          <DateInputField
            name="event_date"
            onChange={(e) => updateField('event_date', e.target.value)}
            defaultValue={formData.event_date}
            label="Event From Date"
            placeholder="Select Event From Date"
            fromDate={getTodayDate()}
            isInvalid={showErrors && !!errors.event_date}
            errorMessage={showErrors ? errors.event_date : ''}
            important
            forceRemount={isOpen}
          />
        </Box>
        <Box flex={1}>
          <DateInputField
            name="event_to_date"
            onChange={(e) => updateField('event_to_date', e.target.value)}
            defaultValue={formData.event_to_date}
            label="Event To Date"
            placeholder="Select Event To Date"
            fromDate={formData.event_date || getTodayDate()}
            isInvalid={showErrors && !!errors.event_to_date}
            errorMessage={showErrors ? errors.event_to_date : ''}
            important
            forceRemount={isOpen}
          />
        </Box>
      </HStack>
    </VStack>
  );

  const modalFooter = () => (
    <HStack
      justifyContent="space-between"
      width="100%"
    >
      <CustomButton
        text="Cancel"
        onPress={handleClose}
        isOutline
        bgColor={COLORS.BLACK_COLOR}
        textColor={COLORS.BLACK_COLOR}
      />
      <CustomButton
        text={`Send Email Enquiry (${selectedHotels.length})`}
        onPress={handleSendEmail}
        isLoading={isSending}
        disabled={!isFormValid()}
      />
    </HStack>
  );

  return (
    <>
      {!MobileWindow ? (
        <CustomModal
          isOpen={isOpen}
          onClose={handleClose}
          header="Email Enquiry"
          body={modalBody()}
          footer={modalFooter()}
          width="60%"
        />
      ) : (
        <MobileModelSheet
          isOpen={isOpen}
          onClose={handleClose}
          header="Email Enquiry"
          body={modalBody()}
          footer={modalFooter()}
          maxH="80vh"
        />
      )}
      
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onProceed={handleConfirmSendEmail}
        selectedHotelsCount={selectedHotels.length}
        isLoading={isSending}
      />
    </>
  );
};

export default EmailEnquiryModal; 